// 语音消息播放器——发送前预览、消息气泡里收发双向渲染复用同一个组件。
// 播放/暂停 + 进度条(可点击跳转) + 时长，亮暗主题都走 --lc-*/--widget-main-color
// token，不需要单独处理暗色（复用现有变量层，见 theme-color.js 的 dark 引擎）。

import React, { useRef, useState, useEffect } from 'react';
import { formatVoiceDuration } from './voice-recorder.js';

export default function VoicePlayer({ src, durationHint = 0, compact = false }) {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    // Chrome 对 MediaRecorder 直出的 webm blob 有已知 bug：<audio> 的
    // duration 有时报 Infinity/NaN，一直拿不到真实总时长。用录制时测出来的
    // durationHint 兜底（访客自己刚发的那条永远有这个值），历史消息/对方发的
    // 没有 hint 时如果撞上这个 bug，只能退化成不显示总时长——已知限制，
    // 真正修需要重新封装容器，不在本次任务范围内。
    const [duration, setDuration] = useState(durationHint || 0);

    useEffect(() => {
        setCurrentTime(0);
        setPlaying(false);
        setDuration(durationHint || 0);
    }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) {
            audio.pause();
        } else {
            audio.play().catch(() => {});
        }
    };

    const onSeek = (e) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        audio.currentTime = ratio * duration;
        setCurrentTime(audio.currentTime);
    };

    const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;
    const label = formatVoiceDuration(playing || currentTime > 0 ? currentTime : duration);

    return (
        <div className="lc-voice-player" style={{
            display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)',
            minWidth: compact ? '150px' : '190px',
            maxWidth: '240px',
            padding: '6px 10px',
            borderRadius: 'var(--lc-radius-lg)',
            background: 'var(--lc-surface-2, #f0f0f0)',
        }}>
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => { setPlaying(false); setCurrentTime(0); }}
                onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                onLoadedMetadata={(e) => {
                    const d = e.target.duration;
                    if (isFinite(d) && d > 0) setDuration(d);
                }}
                style={{ display: 'none' }}
            />
            <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? 'pause' : 'play'}
                style={{
                    flexShrink: 0, width: '26px', height: '26px', borderRadius: 'var(--lc-radius-full)',
                    border: 'none', background: 'var(--widget-main-color, #2563eb)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    padding: 0,
                }}
            >
                {playing ? (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>
                ) : (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '1px' }}><polygon points="6 4 20 12 6 20"/></svg>
                )}
            </button>
            <div
                onClick={onSeek}
                style={{
                    flex: 1, height: '4px', borderRadius: 'var(--lc-radius-xs)',
                    background: 'var(--lc-border, #cbd5e1)',
                    cursor: duration ? 'pointer' : 'default',
                    position: 'relative',
                }}
            >
                <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: `${progress * 100}%`, borderRadius: 'var(--lc-radius-xs)',
                    background: 'var(--widget-main-color, #2563eb)',
                }} />
            </div>
            <span style={{
                flexShrink: 0, fontSize: '11px', fontVariantNumeric: 'tabular-nums',
                color: 'var(--lc-text-2, #475569)', minWidth: '26px', textAlign: 'right',
            }}>
                {label}
            </span>
        </div>
    );
}
